import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { createSelfAccount } from '../../services/studentService';
import Button from '../../components/common/Button/Button';
import cosmicLogo from '../../assets/images/cosmic-logo.png';
import styles from './StudentOnboarding.module.css';

const StudentOnboarding = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    phone: '',
    whatsapp_phone: '',
    school: '',
    graduation_year: '',
    program_enrolled: '',
    field_interest_tags: '',
    college_aspirations_major: '',
    college_aspirations_region: '',
    project_themes_interested: '',
    board_curriculum: '',
    city: '',
    country: '',
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    shipping_address: '',
    competitions_suggested: '',
    competitions_won: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.details_added) {
      navigate('/student/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({
        ...prev,
        email: user.email
      }));
    }
  }, [user?.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        graduation_year: parseInt(formData.graduation_year, 10) || null,
        field_interest_tags: formData.field_interest_tags ? formData.field_interest_tags.split(',').map((s) => s.trim()).filter(Boolean) : [],
        project_themes_interested: formData.project_themes_interested ? formData.project_themes_interested.split(',').map((s) => s.trim()).filter(Boolean) : [],
        competitions_suggested: formData.competitions_suggested ? formData.competitions_suggested.split(',').map((s) => s.trim()).filter(Boolean) : [],
        competitions_won: formData.competitions_won ? formData.competitions_won.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };

      if (!payload.whatsapp_phone) {
        delete payload.whatsapp_phone;
      }
      delete payload.email;

      await createSelfAccount(payload);
      await refreshUser();
      navigate('/student/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete onboarding. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className={styles.onboardingContainer}>
      <div className={styles.onboardingCard}>
        <div className={styles.header}>
          <img src={cosmicLogo} alt="COSMIC Logo" className={styles.logoImage} />
          <h1 className={styles.logo}>COSMIC</h1>
          <p className={styles.subtitle}>Complete Your Student Profile</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="first_name" className={styles.label}>First Name *</label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleChange}
                className={styles.input}
                placeholder="First Name"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="last_name" className={styles.label}>Last Name *</label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleChange}
                className={styles.input}
                placeholder="Last Name"
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>Email *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              className={styles.input}
              disabled
              required
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="phone" className={styles.label}>Phone Number *</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="+1234567890"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="whatsapp_phone" className={styles.label}>WhatsApp Number</label>
              <input
                id="whatsapp_phone"
                name="whatsapp_phone"
                type="tel"
                value={formData.whatsapp_phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="+1234567890"
              />
            </div>
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="school" className={styles.label}>School</label>
              <input
                id="school"
                name="school"
                type="text"
                value={formData.school}
                onChange={handleChange}
                className={styles.input}
                placeholder="Name of your school"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="graduation_year" className={styles.label}>Graduation Year</label>
              <input
                id="graduation_year"
                name="graduation_year"
                type="number"
                value={formData.graduation_year}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g., 2026"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="city" className={styles.label}>City</label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                className={styles.input}
                placeholder="City"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="country" className={styles.label}>Country</label>
              <input
                id="country"
                name="country"
                type="text"
                value={formData.country}
                onChange={handleChange}
                className={styles.input}
                placeholder="Country"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="board_curriculum" className={styles.label}>Board/Curriculum</label>
            <input
              id="board_curriculum"
              name="board_curriculum"
              type="text"
              value={formData.board_curriculum}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., AP, IB, CBSE, ICSE"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="college_aspirations_major" className={styles.label}>College Aspiration (Major)</label>
              <input
                id="college_aspirations_major"
                name="college_aspirations_major"
                type="text"
                value={formData.college_aspirations_major}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g., Computer Science"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="college_aspirations_region" className={styles.label}>College Aspiration (Region)</label>
              <input
                id="college_aspirations_region"
                name="college_aspirations_region"
                type="text"
                value={formData.college_aspirations_region}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g., USA, UK"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="field_interest_tags" className={styles.label}>Fields of Interest (comma separated)</label>
            <input
              id="field_interest_tags"
              name="field_interest_tags"
              type="text"
              value={formData.field_interest_tags}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., Robotics, AI, Business"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="project_themes_interested" className={styles.label}>Project Themes (comma separated)</label>
            <input
              id="project_themes_interested"
              name="project_themes_interested"
              type="text"
              value={formData.project_themes_interested}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., Web App, AI Model"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="program_enrolled" className={styles.label}>Program Enrolled</label>
            <input
              id="program_enrolled"
              name="program_enrolled"
              type="text"
              value={formData.program_enrolled}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., COSMIC Research Program"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="parent_name" className={styles.label}>Parent/Guardian Name</label>
              <input
                id="parent_name"
                name="parent_name"
                type="text"
                value={formData.parent_name}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="parent_phone" className={styles.label}>Parent/Guardian Phone</label>
              <input
                id="parent_phone"
                name="parent_phone"
                type="tel"
                value={formData.parent_phone}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="parent_email" className={styles.label}>Parent/Guardian Email</label>
            <input
              id="parent_email"
              name="parent_email"
              type="email"
              value={formData.parent_email}
              onChange={handleChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="shipping_address" className={styles.label}>Shipping Address</label>
            <textarea
              id="shipping_address"
              name="shipping_address"
              value={formData.shipping_address}
              onChange={handleChange}
              className={styles.textarea}
              placeholder="Full shipping address"
              rows="3"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="competitions_won" className={styles.label}>Competitions Won (comma separated)</label>
            <input
              id="competitions_won"
              name="competitions_won"
              type="text"
              value={formData.competitions_won}
              onChange={handleChange}
              className={styles.input}
            />
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <Button type="submit" loading={loading}
            variant="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Saving Profile...' : 'Complete Onboarding'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default StudentOnboarding;
